import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { getLeadSpecificDepositMethodsApi } from '../../../../utils/api/Lead/LeadSpecificOverrideApi/getLeadSpecificDepositMethodsApi';
import { createLeadSpecificDepositMethodApi } from '../../../../utils/api/Lead/LeadSpecificOverrideApi/createLeadSpecificDepositMethodApi';
import { updateLeadSpecificDepositMethodApi } from '../../../../utils/api/Lead/LeadSpecificOverrideApi/updateLeadSpecificDepositMethodApi';
import { deleteLeadSpecificDepositMethodApi } from '../../../../utils/api/Lead/LeadSpecificOverrideApi/deleteLeadSpecificDepositMethodApi';
import { getDepositMethodsApi } from '../../../../utils/api/Lead/depositMethods/getDepositMethodsApi';
import { getMyAccountApi } from '../../../../utils/api/Account/getMyAccountApi';
import { OverrideDepositMethodModal } from '../../../../components/Modal/ResolvedDepositMethods/OverrideDepositMethodModal';
import { PermissionDenied } from '../../../../components/UI/Custom/PermissionDenied';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Notification } from '../../../../components/UI/Notifications';
import { LoadingCircle } from '../../../../components/UI/Custom/LoadingCircle';
import styles from "../../../../styles/pages/Lead/LeadManagement/ResolvedDepositMethodsPage.module.css"
import { LangContext } from '../../../../context/LangContext';
import { ThemeContext } from '../../../../context/ThemeContext';

const translations = {
    ru: {
        title: (id) => `Методы Депозита для Лида #${id}`,
        noMethods: 'Для этого лида не найдено индивидуальных методов депозита.',
        key: 'Ключ',
        type: 'Тип',
        label: 'Название',
        address: 'Адрес',
        html: 'HTML Контент',
        icon: 'Иконка',
        sort: 'Сортировка',
        status: 'Статус',
        hidden: 'Скрыт',
        actions: 'Действия',
        activeStatus: 'Активен',
        inactiveStatus: 'Неактивен',
        hiddenStatus: 'Скрыт',
        visibleStatus: 'Виден',
        addMethod: 'Создать метод',
        edit: 'Изменить',
        delete: 'Удалить',
        createSuccess: 'Индивидуальный метод успешно создан.',
        updateSuccess: 'Индивидуальный метод успешно обновлён.',
        deleteSuccess: 'Индивидуальный метод успешно удалён.',
        deleteConfirm: 'Удалить этот индивидуальный метод?',
        savingError: 'Ошибка при сохранении индивидуального метода.',
        loadingError: 'Ошибка при загрузке методов депозита.',
        accessDenied: 'Доступ к индивидуальным методам депозита для лида отсутствует.',
    },
    en: {
        title: (id) => `Deposit Methods for Lead #${id}`,
        noMethods: 'No individual deposit methods found for this lead.',
        key: 'Key',
        type: 'Type',
        label: 'Label',
        address: 'Address',
        html: 'HTML Content',
        icon: 'Icon',
        sort: 'Sort',
        status: 'Status',
        hidden: 'Hidden',
        actions: 'Actions',
        activeStatus: 'Active',
        inactiveStatus: 'Inactive',
        hiddenStatus: 'Hidden',
        visibleStatus: 'Visible',
        addMethod: 'Create Method',
        edit: 'Edit',
        delete: 'Delete',
        createSuccess: 'Individual method has been created.',
        updateSuccess: 'Individual method has been updated.',
        deleteSuccess: 'Individual method has been deleted.',
        deleteConfirm: 'Delete this individual method?',
        savingError: 'Error saving individual method.',
        loadingError: 'Error loading deposit methods.',
        accessDenied: 'Access to lead individual deposit methods is denied.',
    }
};

export const ResolvedDepositMethodsPage = () => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const { id: leadId } = useParams();

    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [methodToEdit, setMethodToEdit] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [availableMethods, setAvailableMethods] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [isPermissionsLoaded, setIsPermissionsLoaded] = useState(false);

    const fetchMethods = useCallback(async () => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const data = await getLeadSpecificDepositMethodsApi(leadId);
            setMethods(data || []);
        } catch (err) {
            setError(err.message || t.loadingError);
            setMethods([]);
        } finally {
            setLoading(false);
        }
    }, [leadId, t]);

    const fetchPermissions = useCallback(async () => {
        try {
            const accountData = await getMyAccountApi();

            if (accountData?.permissions) {
                const permissionsObject = {};
                accountData.permissions.forEach((permission) => {
                    permissionsObject[permission.name] = permission;
                });
                setPermissions(permissionsObject);
            }
        } catch (err) {
            setError(err.message || t.loadingError);
        } finally {
            setIsPermissionsLoaded(true);
        }
    }, [t]);

    const fetchAvailableMethods = useCallback(async () => {
        try {
            const templates = await getDepositMethodsApi(1, 50);
            const templateItems = templates?.items || [];
            const usedKeys = new Set(methods.map((method) => method.method_key));

            setAvailableMethods(templateItems.filter((method) => !usedKeys.has(method.key)));
        } catch {
            setAvailableMethods([]);
        }
    }, [methods]);

    useEffect(() => {
        if (leadId && isPermissionsLoaded && permissions['lead.upsert_lead_deposit_methods']) {
            fetchMethods();
        }
    }, [fetchMethods, leadId, isPermissionsLoaded, permissions]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    useEffect(() => {
        if (isPermissionsLoaded && permissions['lead.upsert_lead_deposit_methods']) {
            fetchAvailableMethods();
        }
    }, [fetchAvailableMethods, isPermissionsLoaded, permissions]);

    const renderActiveStatus = (isActive) => {
        return isActive
            ? <span className={`${styles.statusActive} ${theme === 'dark' ? styles.darkStatusActive : ''}`}><CheckCircleOutlineIcon fontSize="small" /> {t.activeStatus}</span>
            : <span className={`${styles.statusInactive} ${theme === 'dark' ? styles.darkStatusInactive : ''}`}><CancelOutlinedIcon fontSize="small" /> {t.inactiveStatus}</span>;
    }

    const renderHiddenStatus = (isHidden) => {
        return isHidden
            ? <span className={`${styles.statusInactive} ${theme === 'dark' ? styles.darkStatusInactive : ''}`}><VisibilityOffOutlinedIcon fontSize="small" /> {t.hiddenStatus}</span>
            : <span className={`${styles.statusActive} ${theme === 'dark' ? styles.darkStatusActive : ''}`}><CheckCircleOutlineIcon fontSize="small" /> {t.visibleStatus}</span>;
    };

    const handleEdit = (method) => {
        setError(null);
        setSuccessMessage(null);
        setMethodToEdit({
            ...method,
            key: method.method_key,
            content_html: method.content_html,
        });
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setError(null);
        setSuccessMessage(null);
        setMethodToEdit({
            method_key: '',
            type: 'crypto',
            label: '',
            sort: 0,
            address: '',
            content_html: '',
            is_active: true,
            is_hidden: false,
            icon: null,
            icon_preview: '',
            methodOptions: availableMethods,
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setMethodToEdit(null);
    };

    const handleSaveOverride = async (formData, id) => {
        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const dataToSend = {
                method_key: id || formData.method_key,
                type: formData.type,
                label: formData.label,
                sort: Number(formData.sort),
                address: formData.address,
                content_html: formData.content_html,
                is_active: formData.is_active !== undefined ? Boolean(formData.is_active) : null,
                is_hidden: formData.is_hidden !== undefined ? Boolean(formData.is_hidden) : null,
                icon: formData.icon,
            };

            if (methodToEdit?.id) {
                await updateLeadSpecificDepositMethodApi(leadId, methodToEdit.id, {
                    address: dataToSend.address,
                    content_html: dataToSend.content_html,
                    is_active: dataToSend.is_active,
                    is_hidden: dataToSend.is_hidden,
                    icon: dataToSend.icon,
                });
                setSuccessMessage(t.updateSuccess);
            } else {
                await createLeadSpecificDepositMethodApi(leadId, dataToSend);
                setSuccessMessage(t.createSuccess);
            }

            handleCloseModal();
            fetchMethods();
        } catch (err) {
            setError(err.message || t.savingError);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteMethod = async (method) => {
        if (!method?.id) {
            return;
        }

        const confirmed = window.confirm(t.deleteConfirm);

        if (!confirmed) {
            return;
        }

        setIsDeleting(true);
        setError(null);
        setSuccessMessage(null);

        try {
            await deleteLeadSpecificDepositMethodApi(leadId, method.id);
            setSuccessMessage(t.deleteSuccess);
            fetchMethods();
        } catch (err) {
            setError(err.message || t.loadingError);
        } finally {
            setIsDeleting(false);
        }
    };

    const tableHeaders = [
        { label: t.key, key: 'method_key' },
        { label: t.type, key: 'type' },
        { label: t.label, key: 'label' },
        { label: t.address, key: 'address' },
        { label: t.html, key: 'content_html' },
        { label: t.icon, key: 'icon' },
        { label: t.sort, key: 'sort' },
        { label: t.status, key: 'is_active' },
        { label: t.hidden, key: 'is_hidden' },
        { label: t.actions, key: 'actions' },
    ];

    const containerClass = `${styles.container} ${theme === 'dark' ? styles.darkContainer : ''}`;
    const contentClass = `${styles.content} ${theme === 'dark' ? styles.darkContent : ''}`;
    const tableClass = `${styles.table} ${theme === 'dark' ? styles.darkTable : ''}`;
    const tableHeaderClass = `${styles.tableHeader} ${theme === 'dark' ? styles.darkTableHeader : ''}`;
    const editBtnClass = `${styles.editButton} ${theme === 'dark' ? styles.darkEditButton : ''}`;
    const deleteBtnClass = `${styles.deleteButton} ${theme === 'dark' ? styles.darkDeleteButton : ''}`;

    if (!isPermissionsLoaded) {
        return (
            <div className={styles.loadingBlock}>
                <LoadingCircle size={30} thickness={1} />
            </div>
        );
    }

    if (!permissions['lead.upsert_lead_deposit_methods']) {
        return <PermissionDenied text={t.accessDenied} />;
    }

    return (
        <div className={containerClass}>
            <h2 className={theme === 'dark' ? styles.darkH2 : ''}>
                <PaidOutlinedIcon fontSize="large" className={`${styles.headerIcon} ${theme === 'dark' ? styles.darkHeaderIcon : ''}`} />
                {t.title(leadId)}
            </h2>
            {successMessage && <Notification message={successMessage} type="success" />}
            {error && <Notification message={error} type="error" />}
            <div className={contentClass}>
                <div className={styles.actionBar}>
                    <button
                        onClick={handleCreate}
                        className={editBtnClass}
                    >
                        <AddIcon fontSize="small" style={{ marginRight: '5px' }} />
                        {t.addMethod}
                    </button>
                </div>
                {loading ? (
                    <div className={styles.loadingBlock}>
                        <LoadingCircle size={30} thickness={1} />
                    </div>
                ) : (
                    <div className={styles.tableWrapper}>
                        <table className={tableClass}>
                            <thead className={tableHeaderClass}>
                                <tr>
                                    {tableHeaders.map((header) => (
                                        <th key={header.key} className={styles.tableHeaderTh}>
                                            {header.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {methods.length > 0 ? (
                                    methods.map((method, index) => (
                                        <tr key={method.id || index} className={`${styles.tableRow} ${theme === 'dark' ? styles.darkTableRow : ''}`}>
                                            <td className={styles.tableCell}>{method.method_key}</td>
                                            <td className={styles.tableCell}>{method.type}</td>
                                            <td className={styles.tableCell}>{method.label}</td>
                                            <td className={styles.tableCell}>{method.address}</td>
                                            <td className={styles.tableCell} dangerouslySetInnerHTML={{ __html: method.content_html || '' }} />
                                            <td className={styles.tableCell}>
                                                {method.icon ? (
                                                    <img src={method.icon} alt={method.label} className={styles.methodIcon} />
                                                ) : (
                                                    <span className={styles.emptyIcon}>-</span>
                                                )}
                                            </td>
                                            <td className={styles.tableCell}>{method.sort}</td>
                                            <td className={styles.tableCell}>{renderActiveStatus(method.is_active)}</td>
                                            <td className={styles.tableCell}>{renderHiddenStatus(method.is_hidden)}</td>
                                            <td className={styles.tableCell}>
                                                <div className={styles.actionButtonsContainer}>
                                                    <button
                                                        onClick={() => handleEdit(method)}
                                                        className={editBtnClass}
                                                        disabled={isDeleting}
                                                    >
                                                        <EditIcon fontSize="small" style={{ marginRight: '5px' }} />
                                                        {t.edit}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMethod(method)}
                                                        className={deleteBtnClass}
                                                        disabled={isDeleting || isSaving}
                                                    >
                                                        <DeleteIcon fontSize="small" style={{ marginRight: '5px' }} />
                                                        {isDeleting ? <LoadingCircle size={16} thickness={2} /> : t.delete}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={tableHeaders.length} className={`${styles.noDataMessage} ${theme === 'dark' ? styles.darkNoDataMessage : ''}`}>
                                            {t.noMethods}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {isModalOpen && methodToEdit && (
                <OverrideDepositMethodModal
                    open={isModalOpen}
                    handleClose={handleCloseModal}
                    handleSave={handleSaveOverride}
                    isSubmitting={isSaving}
                    initialData={methodToEdit}
                    theme={theme}
                />
            )}
        </div>
    );
};
